// Primary surface: the authed user's medication list.
// Loads on mount via the session JWT. Each medication renders as a
// rich card with a color-coded pill icon and a status pill. Empty
// state has an illustrated CTA. The "Add a medicine" primary button
// is always visible at the top of the list.

import type { Medication } from '@medreview/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { listMedications } from '../api/medications';
import { AmazonReorderLink } from '../components/AmazonReorderLink';
import { ReadAloud } from '../components/ReadAloud';
import { ApiError } from '../components/senior/ApiError';
import { SeniorBanner } from '../components/senior/SeniorBanner';
import { type AuthedFetch, UnauthenticatedError, useAuthedFetch } from '../hooks/useAuthedFetch';
import { useCelebrate } from '../hooks/useCelebrate';
import { useSubjectName } from '../hooks/useSubjectName';
import { AppPage } from '../layouts/AppPage';
import { drugClassColor } from '../lib/drugClassColor';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'unauthenticated' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; meds: Medication[] };

export function Medications() {
  const authedFetch = useAuthedFetch();
  const subject = useSubjectName();
  const location = useLocation();
  const navigate = useNavigate();
  const celebrate = useCelebrate();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  // Guard against double-fire under React 18 strict-mode double effects
  // and against subsequent re-renders re-celebrating the same arrival.
  const celebratedFor = useRef<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: location.state, celebrate, navigate intentionally excluded; effect should only re-run on auth change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meds = await listMedications(authedFetch);
        if (!cancelled) {
          setState({ kind: 'loaded', meds });
          // If the user just landed here after a successful add (via
          // AddByName/AddByPhoto/AddByBarcode passing
          // location.state.celebrate), fire the milestone. First-ever
          // medicine gets the bigger 'first-med' line; subsequent adds
          // get the generic 'med-added' confetti without overpromising.
          const cel = (location.state as { celebrate?: string } | null)?.celebrate;
          const key = `${cel ?? ''}:${meds.length}`;
          if (cel && celebratedFor.current !== key) {
            celebratedFor.current = key;
            if (cel === 'med-added' && meds.length === 1) {
              celebrate('first-med');
            } else if (cel === 'med-added') {
              celebrate('generic', 'Medicine added to your list.');
            }
            // Clear location.state so a soft-nav back here later
            // doesn't re-fire the celebration.
            navigate(location.pathname, { replace: true, state: null });
          }
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof UnauthenticatedError) {
          setState({ kind: 'unauthenticated' });
        } else {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  return (
    <AppPage
      title={subject.possessive ? `${subject.possessive} medicines` : 'My medicines'}
      back={{ to: '/', label: 'Home' }}
    >
      {state.kind === 'loading' ? <MedListSkeleton /> : null}

      {state.kind === 'unauthenticated' ? (
        <SeniorBanner title="Please open from your Shopify Admin" tone="info">
          MedReview runs inside your OTCHealthMart store as a Shopify embedded app. Open it from the
          Apps section of your Shopify Admin to access your medication list.
        </SeniorBanner>
      ) : null}

      {state.kind === 'error' ? (
        <ApiError
          title="Could not load your medicines"
          error={state.message}
          onRetry={() => window.location.reload()}
        />
      ) : null}

      {state.kind === 'loaded' && state.meds.length === 0 ? <EmptyMedications /> : null}

      {state.kind === 'loaded' && state.meds.length > 0 ? (
        <>
          <div className="meds-actions">
            <Link to="/medications/add-by-barcode" className="home-hero__cta">
              + Add a medicine
            </Link>
            <DoctorVisitButton authedFetch={authedFetch} />
          </div>
          <ReadAloud label="Listen to my list" text={medListSpoken(state.meds)} />
          <ul className="med-list">
            {state.meds.map((m) => (
              <li key={m.id}>
                <MedCard med={m} />
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </AppPage>
  );
}

function medListSpoken(meds: Medication[]): string {
  const lines = meds.map((m) => {
    const status = m.status === 'ACTIVE' ? 'active' : m.status === 'PAUSED' ? 'paused' : 'stopped';
    const extras = [m.strength, m.frequency].filter(Boolean).join(', ');
    return `${m.name}${extras ? `, ${extras}` : ''}, ${status}.`;
  });
  return `You have ${meds.length} medicine${meds.length === 1 ? '' : 's'} on your list. ${lines.join(' ')}`;
}

function DoctorVisitButton({ authedFetch }: { authedFetch: AuthedFetch }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onClick = async () => {
    if (busy) return;
    setErr(null);
    setBusy(true);
    try {
      const res = await authedFetch('/api/pdf/doctor-visit', { method: 'POST', body: '{}' });
      const json = (await res.json()) as {
        ok: boolean;
        data?: { downloadUrl: string };
        error?: { message: string };
      };
      if (!json.ok || !json.data?.downloadUrl) {
        setErr(json.error?.message ?? 'Could not build the summary right now.');
        return;
      }
      window.open(json.data.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="ghost-cta" onClick={onClick} disabled={busy}>
        {busy ? 'Building your summary...' : 'Doctor visit summary (PDF)'}
      </button>
      {err ? <p className="meds-error">{err}</p> : null}
    </>
  );
}

function MedCard({ med }: { med: Medication }): JSX.Element {
  const authedFetch = useAuthedFetch();
  const initial = (med.name || '?').trim().charAt(0).toUpperCase();
  const palette = drugClassColor(med.drugClasses, med.name);
  const statusClass =
    med.status === 'ACTIVE'
      ? 'status-pill--active'
      : med.status === 'PAUSED'
        ? 'status-pill--paused'
        : 'status-pill--stopped';
  const statusLabel =
    med.status === 'ACTIVE' ? 'Active' : med.status === 'PAUSED' ? 'Paused' : 'Stopped';
  // Pill thumbnail from NLM RxImage (focus group #27). Lazy-loaded once
  // per card; falls back to the colored initial circle if no image found.
  const [pillUrl, setPillUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authedFetch(`/api/medications/${med.id}/image`);
        if (!r.ok) return;
        const j = (await r.json()) as { ok: boolean; data?: { url: string | null } };
        if (!cancelled && j.ok && j.data?.url) setPillUrl(j.data.url);
      } catch {
        // ignore; fallback initial circle is fine
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch, med.id]);
  return (
    <article className="med-card">
      {pillUrl ? (
        <img
          src={pillUrl}
          alt={med.name}
          width={48}
          height={48}
          style={{ borderRadius: '50%', objectFit: 'cover' }}
        />
      ) : (
        <span
          className="med-pill-icon"
          aria-hidden="true"
          style={{ background: palette.bg, color: palette.fg }}
        >
          {initial}
        </span>
      )}
      <div>
        <h3 className="med-card__title">{med.name}</h3>
        <p className="med-card__sub">{[med.strength, med.frequency].filter(Boolean).join(' • ')}</p>
        <AmazonReorderLink name={med.name} />
      </div>
      <span className={`status-pill ${statusClass}`}>{statusLabel}</span>
    </article>
  );
}

function EmptyMedications(): JSX.Element {
  return (
    <div className="empty-state">
      <span className="empty-state__art" aria-hidden="true">
        <svg viewBox="0 0 64 64" role="presentation">
          <title>No medicines yet</title>
          <rect
            x="14"
            y="22"
            width="36"
            height="32"
            rx="6"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
          />
          <rect x="22" y="14" width="20" height="12" rx="3" fill="currentColor" />
          <path
            d="M32 30v16M24 38h16"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      </span>
      <h3 className="empty-state__title">Let's add your first medicine</h3>
      <p className="empty-state__body">
        Take a photo of any bottle and we will read the label for you. We identify the medicine and
        check it against safety guidelines built for older adults.
      </p>
      <Link to="/medications/add-by-barcode" className="home-hero__cta">
        Scan a bottle barcode
      </Link>
    </div>
  );
}

function MedListSkeleton(): JSX.Element {
  return (
    <ul className="med-list" aria-label="Loading medications">
      {[1, 2, 3].map((i) => (
        <li key={i}>
          <article className="med-card" aria-hidden="true">
            <span className="med-pill-icon skeleton" style={{ background: 'transparent' }} />
            <div>
              <div className="skeleton" style={{ height: 22, width: '60%' }} />
              <div className="skeleton" style={{ height: 16, width: '40%', marginTop: 8 }} />
            </div>
            <div className="skeleton" style={{ width: 80, height: 24, borderRadius: 999 }} />
          </article>
        </li>
      ))}
    </ul>
  );
}
